import { create, ApiResponse } from 'apisauce';
import _ from 'lodash';
import Storage from 'utils/Storage';
import { isSsoEnabled } from 'utils/authMode';

type BuildApiOptions = {
  baseURL: string;
  getToken?: () => string | null;
  handle401?: boolean;
};

type RefreshOutcome =
  | { ok: true; accessToken: string }
  /** `endSession`: el SSO rechazó el refresh (la sesión ya no vale). False: fallo temporal. */
  | { ok: false; endSession: boolean };

const SSO_AUTH_ENDPOINT = /(^|\/)api\/authentication\/sso\/(login|confirm|refresh|logout)(\?|$)/i;

/** Rutas de auth del SSO: un 401 aquí es un error del flujo de login, no una sesión caducada. */
export const isSsoAuthEndpoint = ( url = '' ) => SSO_AUTH_ENDPOINT.test( url );

/**
 * Refresh en curso, compartido por TODAS las peticiones que reciben 401 a la vez (C7): una sola
 * llamada a `/sso/refresh` por refresh token. Se libera al terminar; no guarda resultados.
 */
let refreshInFlight: Promise<RefreshOutcome> | null = null;

const buildBaseApi = ( { baseURL, getToken, handle401 = true }: BuildApiOptions ) => {
  const api = create( {
    baseURL,
    timeout: 600000,
  } );

  const clearAndRedirectToLogin = () => {
    Storage.clear( 'grupoimpar:token' );
    Storage.clear( 'grupoimpar:refreshToken' );
    Storage.clear( 'persist:grupoimpar' );
    const isLoginPage = window.location.pathname === '/login';
    if ( !isLoginPage ) {
      setTimeout( () => {
        window.location.replace( '/login' );
      }, 100 );
    }
  };

  const runRefresh = async (): Promise<RefreshOutcome> => {
    const currentRefreshToken = Storage.get( 'grupoimpar:refreshToken' );
    if ( !currentRefreshToken ) return { ok: false, endSession: true };

    try {
      const refreshResponse = await api.axiosInstance.post(
        'api/authentication/sso/refresh',
        { refreshToken: currentRefreshToken },
        { skipAuthRefresh: true } as any,
      );

      const nextAccessToken = refreshResponse?.data?.accessToken;
      const nextRefreshToken = refreshResponse?.data?.refreshToken;
      if ( !nextAccessToken || !nextRefreshToken ) return { ok: false, endSession: true };

      Storage.set( 'grupoimpar:token', nextAccessToken );
      Storage.set( 'grupoimpar:refreshToken', nextRefreshToken );
      return { ok: true, accessToken: nextAccessToken };
    } catch ( refreshError: any ) {
      // 400/401/403: refresh caducado, revocado o sin acceso al portal → la sesión ya no vale.
      // 429, 5xx o sin respuesta (red): puede ser temporal → se conserva la sesión.
      const refreshStatus = refreshError?.response?.status;
      return { ok: false, endSession: [400, 401, 403].includes( refreshStatus ) };
    }
  };

  const refreshSession = () => {
    if ( !refreshInFlight ) {
      refreshInFlight = runRefresh().finally( () => {
        refreshInFlight = null;
      } );
    }
    return refreshInFlight;
  };

  api.axiosInstance.interceptors.response.use(
    ( response ) => response,
    async ( axiosError:any ) => {
      const originalRequest = axiosError?.config || {};
      const status = axiosError?.response?.status;

      if (
        status === 401
        && handle401
        && isSsoEnabled()
        && !originalRequest?.authRefreshRetried
        && !originalRequest?.skipAuthRefresh
        && !isSsoAuthEndpoint( originalRequest?.url )
      ) {
        originalRequest.authRefreshRetried = true;

        const outcome = await refreshSession();
        if ( outcome.ok ) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${outcome.accessToken}`;
          return api.axiosInstance.request( originalRequest );
        }

        if ( outcome.endSession ) {
          console.warn( '[SSO] Refresh rejected, closing local session.' );
          clearAndRedirectToLogin();
        } else {
          // Fallo temporal: esta petición falla, pero la sesión sigue para el siguiente intento.
          console.warn( '[SSO] Refresh failed temporarily, keeping session.' );
          originalRequest.authRefreshKept = true;
        }
        return Promise.reject( axiosError );
      }

      return Promise.reject( axiosError );
    },
  );

  api.addRequestTransform( ( request ) => {
    const token = getToken?.();
    if ( token ) {
      request.headers.Authorization = `Bearer ${token}`;
    }
  } );

  api.addResponseTransform( ( response: ApiResponse<any> ) => {
    const {
      data, ok, status, config,
    } = response;

    if ( !ok ) {
      let errors: string | Record<string, string> = '';

      if ( status === 401 ) {
        // No se cierra la sesión si el 401 es del propio login/confirm (el mensaje del error
        // lo explica: código caducado, sesión de OTP inválida…) o si el refresh falló solo
        // temporalmente.
        const keepSession = isSsoAuthEndpoint( config?.url ) || ( config as any )?.authRefreshKept;
        if ( handle401 && !keepSession ) {
          clearAndRedirectToLogin();
          return;
        }
        errors = '401';
      }
      console.log( 'buildBaseApi-data', data );
      if ( data ) {
        if ( data.form?.errors ) {
          const errorsObject: Record<string, string> = {};
          _.map( data.form.errors, ( error, key: string ) => {
            if ( !_.isEmpty( error ) ) {
              errorsObject[key] = error.join( '\n' );
            }
          } );
          errors = errorsObject;
        } else if ( data.errors ) {
          if ( _.isObject( data.errors ) && !_.isArray( data.errors ) ) {
            const arrayErrors: string[] = [];
            _.map( data.errors, ( error ) => {
              if ( _.isString( error ) ) arrayErrors.push( error );
              else if ( _.isArray( error ) ) arrayErrors.push( error.join( '\n' ) );
            } );
            errors = arrayErrors.join( '\n' );
          } else if ( _.isArray( data.errors ) ) {
            errors = data.errors.join( '\n' );
          } else {
            errors = data.errors;
          }
        } else if ( data.message ) {
          errors = data.message;
        } else if ( data.Message ) {
          errors = data.Message;
        } else if ( data.error_description ) {
          errors = data.error_description;
        } else {
          errors = data;
        }
      }

      if ( !errors || _.isEmpty( errors ) ) {
        errors = 'Se produjo un error al intentar conectarse al servidor';
      }

      if ( config?.method === 'get' && !_.isString( errors ) ) {
        ( response as any ).errors = 'Se produjo un error al intentar conectarse al servidor';
      } else {
        ( response as any ).errors = errors;
      }
    }
  } );

  return api;
};

export default buildBaseApi;
